
import { useState, useEffect, useCallback, useRef } from 'react';

interface Timeslot {
  start_time: string;
  end_time: string;
}

interface BookingCalendarProps {
  timeslotsApi: string;
  appointmentsApi: string;
  texts: {
    title: string;
    subtitle: string;
    stepDateLabel: string;
    stepTimeLabel: string;
    stepConfirmLabel: string;
    prevMonth: string;
    nextMonth: string;
    selectDate: string;
    selectTime: string;
    reviewTitle: string;
    selectedDateLabel: string;
    selectedTimeLabel: string;
    modifyTime: string;
    nameLabel: string;
    namePlaceholder: string;
    phoneLabel: string;
    phonePlaceholder: string;
    notesLabel: string;
    notesPlaceholder: string;
    submitButton: string;
    submitting: string;
    successMessage: string;
    errorMessage: string;
    noSlots: string;
    backButton: string;
  };
  containerStyle?: React.CSSProperties;
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function padZero(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

function formatMonthYear(year: number, month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${months[month]} ${year}`;
}

function getTodayString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${padZero(now.getMonth() + 1)}-${padZero(now.getDate())}`;
}

export default function BookingCalendar({ timeslotsApi, appointmentsApi, texts, containerStyle }: BookingCalendarProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [formData, setFormData] = useState({ customer_name: '', customer_phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const formRef = useRef(formData);
  formRef.current = formData;

  const fetchTimeslots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(timeslotsApi);
      const data = await res.json();
      const slots: Timeslot[] = Array.isArray(data) ? data : (data.data || []);
      setTimeslots(slots);
      return slots;
    } catch {
      setTimeslots([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [timeslotsApi]);

  useEffect(() => {
    fetchTimeslots().then((slots) => {
      if (slots.length === 0) return;
      const today = getTodayString();
      const availableDates = [...new Set(slots.map((s) => s.start_time.split(' ')[0]))].sort();
      const futureDates = availableDates.filter((d) => d >= today);
      const targetDate = futureDates.length > 0 ? futureDates[0] : availableDates[availableDates.length - 1];
      if (targetDate) {
        const parts = targetDate.split('-');
        setCalendarYear(parseInt(parts[0], 10));
        setCalendarMonth(parseInt(parts[1], 10) - 1);
        setSelectedDate(targetDate);
      }
    });
  }, [fetchTimeslots]);

  const availableDatesSet = new Set(timeslots.map((s) => s.start_time.split(' ')[0]));

  const slotsForSelectedDate = selectedDate
    ? timeslots.filter((s) => s.start_time.split(' ')[0] === selectedDate)
    : [];

  const daysInMonth = getDaysInMonth(calendarYear, calendarMonth);
  const firstDay = getFirstDayOfMonth(calendarYear, calendarMonth);

  const calendarCells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);
  while (calendarCells.length % 7 !== 0) calendarCells.push(null);

  const goToPrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  const handleDateClick = (day: number) => {
    const dateStr = `${calendarYear}-${padZero(calendarMonth + 1)}-${padZero(day)}`;
    if (availableDatesSet.has(dateStr)) {
      setSelectedDate(dateStr);
      setSelectedSlot(null);
    }
  };

  const handleSlotClick = (slot: Timeslot) => {
    setSelectedSlot(slot);
    setCurrentStep(2);
  };

  const handleSubmit = async () => {
    if (!selectedSlot || !formData.customer_name.trim() || !formData.customer_phone.trim()) return;
    setSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body: Record<string, string> = {
        customer_name: formData.customer_name.trim(),
        customer_phone: formData.customer_phone.trim(),
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        notes: formData.notes.trim(),
      };
      const res = await fetch(appointmentsApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Booking failed');
      setSubmitStatus('success');
      setSelectedDate(null);
      setSelectedSlot(null);
      setFormData({ customer_name: '', customer_phone: '', notes: '' });
      setCurrentStep(0);
      const newSlots = await fetchTimeslots();
      if (newSlots.length > 0) {
        const today = getTodayString();
        const dates = [...new Set(newSlots.map((s) => s.start_time.split(' ')[0]))].sort();
        const future = dates.filter((d) => d >= today);
        const target = future.length > 0 ? future[0] : dates[dates.length - 1];
        if (target) {
          const p = target.split('-');
          setCalendarYear(parseInt(p[0], 10));
          setCalendarMonth(parseInt(p[1], 10) - 1);
          setSelectedDate(target);
        }
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const extractTime = (datetime: string) => {
    const timePart = datetime.split(' ')[1];
    if (!timePart) return '';
    return timePart.substring(0, 5);
  };

  const steps = [
    { label: texts.stepDateLabel, icon: 'ri-calendar-line' },
    { label: texts.stepTimeLabel, icon: 'ri-time-line' },
    { label: texts.stepConfirmLabel, icon: 'ri-check-double-line' },
  ];

  return (
    <div style={containerStyle} className="w-full">
      {/* Status Messages */}
      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3 animate-fade-in">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-full">
            <i className="ri-check-line text-emerald-600 text-lg"></i>
          </div>
          <p className="text-emerald-700 font-medium text-sm">{texts.successMessage}</p>
          <button onClick={() => setSubmitStatus('idle')} className="ml-auto text-emerald-400 hover:text-emerald-600 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full">
            <i className="ri-error-warning-line text-red-600 text-lg"></i>
          </div>
          <p className="text-red-700 font-medium text-sm">{texts.errorMessage}</p>
          <button onClick={() => setSubmitStatus('idle')} className="ml-auto text-red-400 hover:text-red-600 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Step Indicator */}
      <div className="flex items-center justify-center mb-8">
        {steps.map((step, idx) => (
          <div key={idx} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                  idx <= currentStep
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-200'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {idx < currentStep ? (
                  <i className="ri-check-line text-lg"></i>
                ) : (
                  <i className={`${step.icon} text-lg`}></i>
                )}
              </div>
              <span className={`text-xs mt-2 font-medium whitespace-nowrap ${idx <= currentStep ? 'text-emerald-700' : 'text-gray-400'}`}>
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`w-16 sm:w-24 h-0.5 mx-2 mb-5 transition-all duration-300 ${idx < currentStep ? 'bg-emerald-500' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-10 h-10 border-3 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-4"></div>
          <p className="text-gray-500 text-sm">Loading availability...</p>
        </div>
      ) : (
        <>
          {/* Step 0 & 1: Calendar + Time Slots */}
          {currentStep <= 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Calendar */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-5">
                  <button
                    onClick={goToPrevMonth}
                    className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                    aria-label={texts.prevMonth}
                  >
                    <i className="ri-arrow-left-s-line text-xl"></i>
                  </button>
                  <h3 className="text-base font-semibold text-gray-800">
                    {formatMonthYear(calendarYear, calendarMonth)}
                  </h3>
                  <button
                    onClick={goToNextMonth}
                    className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                    aria-label={texts.nextMonth}
                  >
                    <i className="ri-arrow-right-s-line text-xl"></i>
                  </button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 mb-2">
                  {DAYS_OF_WEEK.map((d) => (
                    <div key={d} className="text-center text-xs font-semibold text-gray-400 py-2 uppercase tracking-wider">
                      {d}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7">
                  {calendarCells.map((day, idx) => {
                    if (day === null) {
                      return <div key={`empty-${idx}`} className="p-1" />;
                    }
                    const dateStr = `${calendarYear}-${padZero(calendarMonth + 1)}-${padZero(day)}`;
                    const isAvailable = availableDatesSet.has(dateStr);
                    const isSelected = dateStr === selectedDate;
                    const isToday = dateStr === getTodayString();

                    return (
                      <div key={dateStr} className="p-0.5">
                        <button
                          onClick={() => handleDateClick(day)}
                          disabled={!isAvailable}
                          className={`w-full aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-all duration-200 relative
                            ${isSelected
                              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-200 font-semibold'
                              : isAvailable
                                ? 'hover:bg-emerald-50 text-gray-700 font-medium cursor-pointer'
                                : 'text-gray-300 cursor-default'
                            }
                            ${isToday && !isSelected ? 'ring-2 ring-emerald-300 ring-inset' : ''}
                          `}
                        >
                          <span>{day}</span>
                          {isAvailable && !isSelected && (
                            <span className="w-1 h-1 rounded-full bg-emerald-500 mt-0.5"></span>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Time Slots */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <h3 className="text-base font-semibold text-gray-800 mb-1">{texts.selectTime}</h3>
                {selectedDate && (
                  <p className="text-sm text-gray-500 mb-4">{selectedDate}</p>
                )}

                {!selectedDate ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-50 mb-3">
                      <i className="ri-calendar-check-line text-2xl"></i>
                    </div>
                    <p className="text-sm">{texts.selectDate}</p>
                  </div>
                ) : slotsForSelectedDate.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-50 mb-3">
                      <i className="ri-time-line text-2xl"></i>
                    </div>
                    <p className="text-sm">{texts.noSlots}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1">
                    {slotsForSelectedDate.map((slot, idx) => {
                      const startTime = extractTime(slot.start_time);
                      const endTime = extractTime(slot.end_time);
                      const isActive = selectedSlot?.start_time === slot.start_time;
                      return (
                        <button
                          key={idx}
                          onClick={() => handleSlotClick(slot)}
                          className={`px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 border cursor-pointer
                            ${isActive
                              ? 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-200'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                            }
                          `}
                        >
                          <div className="flex items-center justify-center gap-1.5">
                            <i className={`ri-time-line text-xs ${isActive ? 'text-emerald-200' : 'text-gray-400'}`}></i>
                            <span>{startTime} - {endTime}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Review + Form */}
          {currentStep === 2 && selectedSlot && (
            <div className="max-w-lg mx-auto">
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                {/* Review Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-gray-800">{texts.reviewTitle}</h3>
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 cursor-pointer"
                    >
                      <i className="ri-edit-line text-sm"></i>
                      {texts.modifyTime}
                    </button>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100">
                        <i className="ri-calendar-line text-emerald-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">{texts.selectedDateLabel}</p>
                        <p className="text-sm font-semibold text-gray-800">{selectedDate}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100">
                        <i className="ri-time-line text-emerald-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">{texts.selectedTimeLabel}</p>
                        <p className="text-sm font-semibold text-gray-800">
                          {extractTime(selectedSlot.start_time)} - {extractTime(selectedSlot.end_time)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{texts.nameLabel} <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      placeholder={texts.namePlaceholder}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{texts.phoneLabel} <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      placeholder={texts.phonePlaceholder}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{texts.notesLabel}</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder={texts.notesPlaceholder}
                      maxLength={500}
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {texts.backButton}
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={submitting || !formData.customer_name.trim() || !formData.customer_phone.trim()}
                      className="flex-[2] px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap shadow-md shadow-emerald-200"
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                          {texts.submitting}
                        </span>
                      ) : (
                        texts.submitButton
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
